import classNames from 'classnames'
import { FC, useContext } from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Button, Input } from 'antd'
import { useRouter } from 'next/router'

import { IconVideoDownloaderUrlInput } from '@/components/common/Icons'
import AmplitudeContext, { AmplitudeType } from '@/components/AmplitudeContext'

type StepOneProps = {
  url: string
  onChangeUrl: (url: string) => void
  onBlurUrlInput: () => void
  onSubmitUrl: () => void
  disabledSubmitButton: boolean
  isShowingError: boolean

  keyword: string

  className?: string
}

export const StepOne: FC<StepOneProps> = ({
  url,
  onChangeUrl,
  onBlurUrlInput,
  onSubmitUrl,
  disabledSubmitButton,
  isShowingError,

  keyword,

  className,
}) => {
  const { t } = useTranslation('common')

  const { track }  = useContext(AmplitudeContext) as AmplitudeType;
  const router = useRouter();

  return (
    <StepOneContainer className={className}>
      <StepOneTitleAndDescription>
        <h2>{t('Download a {{keyword}} Video', { keyword })}</h2>
        <p>
          {t(
            'Download any public video from {{keyword}}, then edit, save, and share it wherever you like!',
            { keyword },
          )}
        </p>
      </StepOneTitleAndDescription>

      <UrlInputForm
        onSubmit={(e) => {
          e.preventDefault()

          track("get_converted", {
            page_path: router.asPath
          });

          if (window?.dataLayer?.push) {
            window.dataLayer.push({
              event: "get_converted",
              page_path: router.asPath
            });
          }

          onSubmitUrl()
        }}
      >
        <UrlInputIconAndInput>
          <UrlInputIconContainer>
            <IconVideoDownloaderUrlInput />
          </UrlInputIconContainer>
          <UrlInput
            type="text"
            className={classNames({
              error: isShowingError,
            })}
            placeholder={t(
              "Paste the URL of the Youtube or TikTok video you'd like to download.",
            )}
            value={url}
            onChange={(e) => {
              onChangeUrl(e.target.value)
            }}
            onBlur={onBlurUrlInput}
          />
        </UrlInputIconAndInput>
        <UrlInputButton
          size="large"
          type="primary"
          htmlType="submit"
          disabled={disabledSubmitButton}
        >
          {t('Download')}
        </UrlInputButton>
      </UrlInputForm>

      <NoCreditCard>{t('No Credit Card Required')}</NoCreditCard>
    </StepOneContainer>
  )
}

const StepOneContainer = styled.div`
  padding: 20px 40px 10px 40px;

  @media (max-width: ${({ theme }) => theme.screenMD}px) {
    padding: 40px 20px;
  }
`

const StepOneTitleAndDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  margin-bottom: 20px;

  h2 {
    font-size: 28px;
    line-height: 40px;
    font-weight: 700;
    color: #000000;
    letter-spacing: -2.5%;
    text-align: center;

    margin: 0;
  }

  p {
    font-size: 16px;
    line-height: 22px;
    color: #000000;
    text-align: center;

    margin: 0;
  }

  @media (max-width: ${({ theme }) => theme.screenMD}px) {
    h2 {
      font-size: 24px;
      line-height: 33.6px;
    }
  }
`

const UrlInputForm = styled.form`
  display: flex;
  column-gap: 20px;
  row-gap: 8px;
  justify-content: stretch;
  align-items: center;

  border: 1px solid #000000;
  border-radius: 10px;
  padding: 4px 10px;
  margin-bottom: 10px;

  @media (max-width: ${({ theme }) => theme.screenMD}px) {
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
  }
`

const UrlInputIconAndInput = styled.div`
  flex: 1;

  display: flex;
  gap: 20px;
`

const UrlInputIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

/**
 * &.error: 에러인 경우
 */
const UrlInput = styled(Input)`
  flex: 1;
  align-self: stretch;

  border: 0;

  font-size: 16px;
  line-height: 22px;

  text-overflow: ellipsis;

  &.error {
    color: #ff0000;
  }
`

const UrlInputButton = styled(Button)`
  flex: 0;

  width: 120px;

  padding: 0px 24px;
  height: 40px;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;

  @media (max-width: ${({ theme }) => theme.screenMD}px) {
    flex-basis: 40px;

    width: 100%;
  }
`

const NoCreditCard = styled.div`
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  letter-spacing: -2.5%;
  color: #146ef5;
  text-align: center;

  margin-bottom: 20px;
`
